import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

const giftWrapHandle = 'gift-wrap';
const giftWrapTitle = 'gift wrap';
const giftWrapVariantId = 53013065597237;

function normalize(value) {
  return String(value || '').trim().toLowerCase();
}

function isGiftWrapItem(item) {
  if (!item) return false;
  const url = normalize(item.url);
  const handle = normalize(item.handle);
  const productHandle = normalize(item.product_handle);
  const title = normalize(item.product_title || item.title);

  return (
    item.variant_id === giftWrapVariantId ||
    handle === giftWrapHandle ||
    productHandle === giftWrapHandle ||
    url.includes(`/products/${giftWrapHandle}`) ||
    title === giftWrapTitle
  );
}

function getDisplayItemCount(cart) {
  if (!cart?.items) return undefined;

  return cart.items.reduce((count, item) => (isGiftWrapItem(item) ? count : count + item.quantity), 0);
}

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value > 0 && value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.addEventListener('click', this.refreshCartBubbleFromCart, { capture: true });
    this.ensureCartBubbleIsCorrect();
    this.refreshCartBubbleFromCart();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
    this.removeEventListener('click', this.refreshCartBubbleFromCart, { capture: true });
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
      this.refreshCartBubbleFromCart();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const displayItemCount = getDisplayItemCount(event.detail.resource);
    const itemCount = displayItemCount ?? event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Fetches the current cart and corrects the bubble from real cart lines.
   */
  refreshCartBubbleFromCart = async () => {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;

      const cart = await response.json();
      const displayItemCount = getDisplayItemCount(cart);
      if (displayItemCount === undefined) return;

      this.renderCartBubble(displayItemCount, false, false);
    } catch (_) {
      // no-op
    }
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of visible shopper items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    const nextCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.refs.cartBubbleCount.classList.toggle('hidden', nextCartCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', nextCartCount === 0);

    this.currentCartCount = nextCartCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', nextCartCount > 0);

    if (nextCartCount > 0) {
      sessionStorage.setItem(
        'cart-count',
        JSON.stringify({
          value: String(nextCartCount),
          timestamp: Date.now(),
        })
      );
    } else {
      sessionStorage.removeItem('cart-count');
    }

    if (!animate || nextCartCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
          this.refreshCartBubbleFromCart();
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
